import { useState } from "react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Calendar,
  Maximize2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "../components/ui/dialog";

// Before/After Project Data Structure
// Easy to update with real photos later
export const BEFORE_AFTER_PROJECTS = {
  yvelines: [
    {
      id: "projet-1",
      title: "Rénovation toiture complète",
      location: "Saint-Germain-en-Laye (78)",
      date: "2024",
      category: "Couverture",
      description: "Réfection totale d'une toiture en tuiles plates sur maison de caractère. Remplacement de la couverture, révision de la zinguerie et isolation.",
      beforeImage: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80",
      afterImage: "https://images.unsplash.com/photo-1513584684374-8bab748fbf90?w=800&q=80",
      beforeAlt: "Toiture avant rénovation - tuiles abîmées",
      afterAlt: "Toiture après rénovation - tuiles neuves",
    },
    {
      id: "projet-2",
      title: "Ravalement façade pavillon",
      location: "Le Pecq (78)",
      date: "2024",
      category: "Façade",
      description: "Ravalement complet avec réparation des fissures, application d'un enduit neuf et peinture de finition. Transformation spectaculaire.",
      beforeImage: "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=800&q=80",
      afterImage: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&q=80",
      beforeAlt: "Façade avant ravalement - crépi abîmé",
      afterAlt: "Façade après ravalement - finition impeccable",
    },
    {
      id: "projet-3",
      title: "Réparation charpente",
      location: "Versailles (78)",
      date: "2023",
      category: "Charpente",
      description: "Renforcement et traitement d'une charpente traditionnelle attaquée par les insectes. Remplacement des pièces endommagées.",
      beforeImage: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80",
      afterImage: "https://images.unsplash.com/photo-1513694203232-719a280e022f?w=800&q=80",
      beforeAlt: "Charpente avant traitement",
      afterAlt: "Charpente après rénovation",
    },
  ],
  "haute-savoie": [
    {
      id: "projet-hs-1",
      title: "Rénovation toiture chalet",
      location: "Annecy (74)",
      date: "2024",
      category: "Couverture",
      description: "Réfection complète de la toiture d'un chalet traditionnel. Pose de tuiles résistantes au gel et renforcement de l'étanchéité pour climat alpin.",
      beforeImage: "https://images.unsplash.com/photo-1449158743715-0a90ebb6d2d8?w=800&q=80",
      afterImage: "https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=800&q=80",
      beforeAlt: "Toiture chalet avant rénovation",
      afterAlt: "Toiture chalet après rénovation",
    },
    {
      id: "projet-hs-2",
      title: "Isolation combles montagne",
      location: "Chamonix (74)",
      date: "2024",
      category: "Isolation",
      description: "Isolation haute performance des combles d'une maison d'altitude. Pose de laine de roche 300mm pour résister aux hivers rigoureux.",
      beforeImage: "https://images.unsplash.com/photo-1597526738251-c73e35b22820?w=800&q=80",
      afterImage: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80",
      beforeAlt: "Combles avant isolation",
      afterAlt: "Combles après isolation",
    },
    {
      id: "projet-hs-3",
      title: "Ravalement maison savoyarde",
      location: "Annemasse (74)",
      date: "2023",
      category: "Façade",
      description: "Ravalement de façade avec enduit résistant au gel. Réparation des fissures dues aux cycles gel/dégel et peinture minérale.",
      beforeImage: "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=800&q=80",
      afterImage: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=80",
      beforeAlt: "Façade avant ravalement",
      afterAlt: "Façade après ravalement",
    },
  ],
};

// Image Comparison Slider Component
const ImageComparisonSlider = ({ beforeImage, afterImage, beforeAlt, afterAlt }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = (e) => {
    if (!isDragging) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percent = Math.max(0, Math.min((x / rect.width) * 100, 100));
    setSliderPosition(percent);
  };

  const handleTouchMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.touches[0].clientX - rect.left, rect.width));
    const percent = Math.max(0, Math.min((x / rect.width) * 100, 100));
    setSliderPosition(percent);
  };

  return (
    <div
      className="relative w-full aspect-[4/3] overflow-hidden cursor-ew-resize select-none"
      onMouseMove={handleMove}
      onMouseDown={() => setIsDragging(true)}
      onMouseUp={() => setIsDragging(false)}
      onMouseLeave={() => setIsDragging(false)}
      onTouchMove={handleTouchMove}
    >
      {/* After Image (Background) */}
      <img
        src={afterImage}
        alt={afterAlt}
        className="absolute inset-0 w-full h-full object-cover"
      />
      
      {/* Before Image (Clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img
          src={beforeImage}
          alt={beforeAlt}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: `${100 / (sliderPosition / 100)}%`, maxWidth: 'none' }}
        />
      </div>
      
      {/* Slider Line */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
      >
        {/* Slider Handle */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
          <div className="flex items-center gap-0.5">
            <ChevronLeft className="w-4 h-4 text-[#0F172A]" />
            <ChevronRight className="w-4 h-4 text-[#0F172A]" />
          </div>
        </div>
      </div>
      
      {/* Labels */}
      <div className="absolute top-4 left-4 bg-[#0F172A]/80 text-white px-3 py-1 text-sm font-bold uppercase tracking-wider">
        Avant
      </div>
      <div className="absolute top-4 right-4 bg-[#C2410C]/90 text-white px-3 py-1 text-sm font-bold uppercase tracking-wider">
        Après
      </div>
    </div>
  );
};

// Simple Before/After Card (without slider)
const BeforeAfterCard = ({ project, onOpenFullscreen }) => {
  const [showAfter, setShowAfter] = useState(false);

  return (
    <Card className="group overflow-hidden border-none shadow-lg hover:shadow-xl transition-shadow">
      <CardContent className="p-0">
        {/* Image Container */}
        <div className="relative aspect-[4/3] overflow-hidden">
          {/* Toggle between before/after on hover or click */}
          <img
            src={showAfter ? project.afterImage : project.beforeImage}
            alt={showAfter ? project.afterAlt : project.beforeAlt}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          
          {/* Labels */}
          <div className={`absolute top-4 left-4 px-3 py-1 text-sm font-bold uppercase tracking-wider transition-all ${
            showAfter ? "bg-[#C2410C] text-white" : "bg-[#0F172A] text-white"
          }`}>
            {showAfter ? "Après" : "Avant"}
          </div>

          {/* Category Badge */}
          <Badge className="absolute top-4 right-4 bg-white/90 text-[#0F172A] border-none">
            {project.category}
          </Badge>

          {/* Toggle Button */}
          <button
            onClick={() => setShowAfter(!showAfter)}
            className="absolute bottom-4 left-4 bg-white/90 hover:bg-white text-[#0F172A] px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors"
          >
            Voir {showAfter ? "Avant" : "Après"}
          </button>

          {/* Fullscreen Button */}
          <button
            onClick={() => onOpenFullscreen(project)}
            className="absolute bottom-4 right-4 bg-white/90 hover:bg-white text-[#0F172A] p-2 transition-colors"
          >
            <Maximize2 className="w-5 h-5" />
          </button>
        </div>

        {/* Info */}
        <div className="p-6">
          <h3 className="font-bold text-xl text-[#0F172A] mb-2 font-['Barlow_Condensed'] uppercase tracking-tight">
            {project.title}
          </h3>
          <div className="flex items-center gap-4 text-sm text-[#64748B] mb-3">
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {project.location}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {project.date}
            </span>
          </div>
          <p className="text-[#64748B] text-sm leading-relaxed">
            {project.description}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Before/After Section Component
const BeforeAfterSection = ({ region }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const projects = BEFORE_AFTER_PROJECTS[region.id] || BEFORE_AFTER_PROJECTS.yvelines;

  return (
    <section
      id="avant-apres"
      className="py-20 md:py-28 bg-white"
      data-testid="before-after-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-[#C2410C] text-white border-none uppercase tracking-wider text-xs">
            Nos transformations
          </Badge>
          <h2 className="section-title text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4 font-['Barlow_Condensed'] mx-auto inline-block">
            Avant / Après
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto mt-8">
            Découvrez nos réalisations en images. Chaque projet témoigne de notre savoir-faire 
            et de notre engagement pour la qualité.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project) => (
            <BeforeAfterCard
              key={project.id}
              project={project}
              onOpenFullscreen={setSelectedProject}
            />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-[#64748B] mb-4">
            Vous souhaitez transformer votre toiture ou façade ?
          </p>
          <a href="#contact">
            <Button className="bg-[#C2410C] hover:bg-[#9A3412] h-12 px-8 uppercase font-bold tracking-wider text-white">
              Demander un devis gratuit
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </a>
        </div>
      </div>

      {/* Fullscreen Modal with Comparison Slider */}
      {selectedProject && (
        <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
          <DialogContent className="max-w-4xl p-0 overflow-hidden">
            <div className="p-6 bg-[#0F172A] text-white">
              <h3 className="font-bold text-2xl font-['Barlow_Condensed'] uppercase tracking-tight">
                {selectedProject.title}
              </h3>
              <p className="text-white/70 text-sm mt-1">
                {selectedProject.location} • {selectedProject.date}
              </p>
            </div>
            <ImageComparisonSlider
              beforeImage={selectedProject.beforeImage}
              afterImage={selectedProject.afterImage}
              beforeAlt={selectedProject.beforeAlt}
              afterAlt={selectedProject.afterAlt}
            />
            <div className="p-6 bg-[#F8FAFC]">
              <p className="text-[#64748B] text-sm">
                <strong className="text-[#0F172A]">Glissez</strong> le curseur pour comparer avant et après.
              </p>
              <p className="text-[#64748B] text-sm mt-2">
                {selectedProject.description}
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
};

export default BeforeAfterSection;
